// 마켓탭 SDK 함수

import { MARKET_TAB_ORDER, MARKET_TAB_USE_COUPON } from '@/constants/analytics/marketTab'

export const useMarketSDK = () => {
  const dayjs = useDayjs()
  type eventData = string | Record<string, any>
  const {
    public: { marketTapId }
  } = useRuntimeConfig()

  const isInitialized = useState<boolean>('mtap:initialized', () => false)

  const initMkt = () => {
    if (!window.mtap || !window.mtap.initialize) {
      console.warn('mtap SDK이 존재하지 않습니다.')
      return
    }

    if (isInitialized.value) {
      return
    }

    window.mtap.initialize({ projectId: marketTapId })
    isInitialized.value = true
  }

  const safeCall = (fn: () => void) => {
    if (!isInitialized.value) {
      console.warn('MarketSDK is not initialized.')
      return
    }
    try {
      fn()
    } catch (err) {
      console.error('MarketSDK error:', err)
    }
  }

  const trackEvent = (eventName: string, eventData?: eventData) => {
    safeCall(() => {
      if (eventData) {
        window.mtap.track(eventName, eventData)
      } else {
        window.mtap.track(eventName)
      }
    })
  }

  const trackPageView = () => {
    safeCall(() => {
      window.mtap.trackPageView()
    })
  }

  const trackUser = (user: Record<string, any>) => {
    safeCall(() => {
      const userCoupons = user.userCoupons.map((couponData: UserCoupons) => ({
        mkt_coupon_id: couponData.couponId,
        mkt_coupon_name: couponData.coupon.couponName,
        mkt_coupon_issued_at: couponData.createdAt,
        mkt_coupon_expired_date: dayjs(couponData.expiredAt).format('YYYY-MM-DD')
      }))

      window.mtap.identify(user.accountId, {
        mkt_grade: user.gradeId,
        mkt_available_points: user.point,
        mkt_email_opt_in: user.isMail,
        mkt_text_message_opt_in: user.isSms,
        mkt_os_push_opt_in: user.isPush,
        mkt_email: user.email,
        mkt_phone_number: user.mobile,
        mkt_name: user.userName,
        last_login_datetime: user.loginedAt,
        mkt_coupons: userCoupons,
        mkt_gender: user.gender || '',
        mkt_birth: user.birthDate ? dayjs(user.birthDate).format('YYYY-MM-DD') : ''
      })
    })
  }

  const trackLogin = (accountId: string) => {
    safeCall(() => {
      window.mtap.login(accountId)
    })
  }

  const trackLogout = () => {
    safeCall(() => {
      window.mtap.logout()
    })
  }

  const trackOrderSuccess = () => {
    safeCall(() => {
      const purchaseData = sessionStorage.getItem(MARKET_TAB_ORDER)
      const couponData = sessionStorage.getItem(MARKET_TAB_USE_COUPON)
      if (purchaseData) {
        const data = JSON.parse(purchaseData)
        trackEvent('mkt_purchase', data)
      }
      if (couponData) {
        const data = JSON.parse(couponData)
        // 이거 쿠폰이 중복 형태로 바뀜
        trackEvent('mkt_use_coupon', {
          mkt_coupon_id: data.couponId,
          mkt_coupon_name: data.couponName
        })
      }
    })
  }

  const clearMtapSession = () => {
    sessionStorage.removeItem(MARKET_TAB_ORDER)
    sessionStorage.removeItem(MARKET_TAB_USE_COUPON)
  }

  const trackSignUp = (data: any) => {
    safeCall(() => {
      window.mtap.signup(
        data.accountId,
        {
          mkt_name: data.userName,
          mkt_email: data.email
        },
        {
          retain_from_adv: false
        },
        true // 회원가입 후 자동 로그인 되는 경우 persistUser: true 로 설정
      )
    })
  }

  return {
    initMkt,
    trackEvent,
    trackPageView,
    trackUser,
    trackLogin,
    trackLogout,
    trackOrderSuccess,
    trackSignUp,
    clearMtapSession
  }
}
